import React, { useMemo, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title as ChartTitle,
  Tooltip,
  Legend,
  ChartOptions,
  Filler
} from 'chart.js';
import { formatTime } from '../../../utils/stringUtils';
import PipeLogsViewer from './PipeLogsViewer';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ChartTitle,
  Tooltip,
  Legend,
  Filler
);

interface PipeStats {
  pipe_id: string;
  pipe_name: string;
  total_requests: number;
  successful_requests: number;
  failed_requests: number;
  avg_latency_ms: number;
  avg_gateway_processing_ms: number;
  avg_target_processing_ms: number;
  total_input_tokens: number;
  total_output_tokens: number;
  total_cost: number;
  last_request_at?: string;
}

export interface PipeMonitoringTabProps {
  pipeId: string;
  pipeName: string;
  state: any;
  actions: any;
}

const PipeMonitoringTab: React.FC<PipeMonitoringTabProps> = ({ 
  pipeId, 
  pipeName,
  state,
  actions
}) => {
  const [selectedBucket, setSelectedBucket] = React.useState<number | null>(null);
  const previousBucketRef = React.useRef<number | null>(null);
  const [chartsKey, setChartsKey] = React.useState(0);

  // Get pipe stats from dashboard
  const pipeStats = useMemo(() => {
    // Get stats from the appropriate view (filtered or global)
    const dashboardStats = state.activeView === 'filtered' 
      ? state.filteredState?.stats 
      : state.globalState?.stats;
      
    const metrics = dashboardStats?.metrics;
    if (!metrics) {
      return null;
    }
    
    // When filtered by pipe_id, the backend might return a single pipe's stats directly
    // or still return it as an array with one item
    const stats = metrics.pipe_stats;
    if (!stats) {
      return null;
    }
    
    // If it's an array, find the matching pipe
    if (Array.isArray(stats)) {
      const found = stats.find((p: PipeStats) => p.pipe_id === pipeId);
      return found || null;
    }
    
    // If it's a single object and matches our pipe, use it
    if ((stats as any).pipe_id === pipeId) {
      return stats as PipeStats;
    }
    
    return null;
  }, [state.activeView, state.globalState, state.filteredState, pipeId]);

  // Extract pipe time series data for traffic graph
  const pipeTimeSeries = useMemo(() => {
    // Get stats from the appropriate view (filtered or global)
    const dashboardStats = state.activeView === 'filtered' 
      ? state.filteredState?.stats 
      : state.globalState?.stats;
      
    const metrics = dashboardStats?.metrics;
    
    if (!metrics || !metrics.time_series) {
      return [];
    }
    
    // When filtering by pipe_id, time_series is already filtered
    return metrics.time_series;
  }, [state.activeView, state.globalState, state.filteredState, pipeId]);

  // Extract pipe latency time series
  const pipeLatencyTimeSeries = useMemo(() => {
    // Get stats from the appropriate view (filtered or global)
    const dashboardStats = state.activeView === 'filtered' 
      ? state.filteredState?.stats 
      : state.globalState?.stats;
      
    const metrics = dashboardStats?.metrics;
    
    if (!metrics || !metrics.latency_time_series) {
      return [];
    }
    
    // When filtering by pipe_id, latency_time_series is already filtered
    return metrics.latency_time_series;
  }, [state.activeView, state.globalState, state.filteredState, pipeId]);

  // Prepare traffic chart data for this pipe (matching channel monitoring format)
  const pipeTrafficChartData = useMemo(() => {
    if (!pipeTimeSeries || pipeTimeSeries.length === 0) {
      return { labels: [], datasets: [] };
    }

    const labels = pipeTimeSeries.map((point: any) => {
      const date = new Date(point.timestamp);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    });
    
    return {
      labels,
      datasets: [
        {
          label: 'Total Requests',
          data: pipeTimeSeries.map((point: any) => point.count || 0),
          borderColor: '#4e73df',
          backgroundColor: 'rgba(78, 115, 223, 0.1)',
          borderWidth: 2,
          pointRadius: 2,
          pointHoverRadius: 4,
          fill: true,
          tension: 0.3,
        },
        {
          label: 'Successful',
          data: pipeTimeSeries.map((point: any) => (point.count || 0) - (point.failed || 0)),
          borderColor: '#1cc88a',
          backgroundColor: 'rgba(28, 200, 138, 0.1)',
          borderWidth: 2,
          pointRadius: 2,
          pointHoverRadius: 4,
          fill: true,
          tension: 0.3,
        },
        {
          label: 'Failed',
          data: pipeTimeSeries.map((point: any) => point.failed || 0),
          borderColor: '#e74a3b',
          backgroundColor: 'rgba(231, 74, 59, 0.1)',
          borderWidth: 2,
          pointRadius: 2,
          pointHoverRadius: 4,
          fill: true,
          tension: 0.3,
        },
      ],
    };
  }, [pipeTimeSeries]);

  // Prepare latency chart data for this pipe
  const pipeLatencyChartData = useMemo(() => {
    if (!pipeLatencyTimeSeries || pipeLatencyTimeSeries.length === 0) {
      return { labels: [], datasets: [] };
    }

    const data = pipeLatencyTimeSeries;
    const labels = data.map((point: { timestamp: string }) => formatTime(point.timestamp));

    return {
      labels,
      datasets: [
        {
          label: 'p50 (Median)',
          data: data.map((point: any) => point.p50_latency_ms || 0),
          borderColor: '#1cc88a',
          backgroundColor: 'rgba(28, 200, 138, 0.1)',
          borderWidth: 2,
          pointRadius: 2,
          pointHoverRadius: 4,
          fill: false,
          tension: 0.3,
        },
        {
          label: 'p95',
          data: data.map((point: any) => point.p95_latency_ms || 0),
          borderColor: '#f6c23e',
          backgroundColor: 'rgba(246, 194, 62, 0.1)',
          borderWidth: 2,
          pointRadius: 2,
          pointHoverRadius: 4,
          fill: false,
          tension: 0.3,
        },
        {
          label: 'p99',
          data: data.map((point: any) => point.p99_latency_ms || 0),
          borderColor: '#e74a3b',
          backgroundColor: 'rgba(231, 74, 59, 0.1)',
          borderWidth: 2,
          pointRadius: 2,
          pointHoverRadius: 4,
          fill: false,
          tension: 0.3,
        },
        {
          label: 'Average',
          data: data.map((point: any) => point.avg_latency_ms || 0),
          borderColor: '#4e73df',
          backgroundColor: 'rgba(78, 115, 223, 0.1)',
          borderWidth: 1,
          pointRadius: 1,
          pointHoverRadius: 3,
          fill: false,
          tension: 0.3,
          borderDash: [5, 5],
        }
      ]
    };
  }, [pipeLatencyTimeSeries]);

  // Sync selectedBucket with settings on mount and when settings change
  React.useEffect(() => {
    if (state.settings?.bucket_seconds && selectedBucket === null) {
      setSelectedBucket(state.settings.bucket_seconds);
    }
  }, [state.settings?.bucket_seconds, selectedBucket]);

  // Handler for bucket selector change
  const handleBucketChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newBucket = parseInt(event.target.value, 10);
    setSelectedBucket(newBucket);
    actions.setDashboardBucketOverride(newBucket);
    
    if (previousBucketRef.current !== newBucket) {
      previousBucketRef.current = newBucket;
      setTimeout(() => {
        actions.loadDashboardStats(true);
      }, 0);
    }
  };

  const trafficChartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
      },
      title: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            return `${context.dataset.label}: ${context.parsed.y}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Requests'
        },
        ticks: {
          precision: 0
        }
      },
      x: {
        title: {
          display: true,
          text: 'Time'
        }
      }
    }
  };

  const latencyChartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
      },
      tooltip: {
        mode: 'index',
        intersect: false,
      },
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: 'Time'
        }
      },
      y: {
        display: true,
        title: {
          display: true,
          text: 'Latency (ms)'
        },
        beginAtZero: true
      }
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    }
  };

  return (
    <div className="pipe-monitoring-tab">
      {/* Stats Cards */}
      <div className="row mb-4">
        <div className="col-xl-3 col-md-6 mb-4">
          <div className="card border-left-primary shadow h-100 py-2">
            <div className="card-body">
              <div className="row no-gutters align-items-center">
                <div className="col mr-2">
                  <div className="text-xs font-weight-bold text-primary text-uppercase mb-1">
                    Total Requests
                  </div>
                  <div className="h5 mb-0 font-weight-bold text-gray-800">
                    {pipeStats?.total_requests?.toLocaleString() || 0}
                  </div>
                </div>
                <div className="col-auto">
                  <i className="fas fa-paper-plane fa-2x text-gray-300"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6 mb-4">
          <div className="card border-left-success shadow h-100 py-2">
            <div className="card-body">
              <div className="row no-gutters align-items-center">
                <div className="col mr-2">
                  <div className="text-xs font-weight-bold text-success text-uppercase mb-1">
                    Success Rate
                  </div>
                  <div className="h5 mb-0 font-weight-bold text-gray-800">
                    {pipeStats && pipeStats.total_requests > 0
                      ? ((pipeStats.successful_requests / pipeStats.total_requests) * 100).toFixed(1)
                      : 0}%
                  </div>
                </div>
                <div className="col-auto">
                  <i className="fas fa-check-circle fa-2x text-gray-300"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6 mb-4">
          <div className="card border-left-info shadow h-100 py-2">
            <div className="card-body">
              <div className="row no-gutters align-items-center">
                <div className="col mr-2">
                  <div className="text-xs font-weight-bold text-info text-uppercase mb-1">
                    Avg Latency
                  </div>
                  <div className="h5 mb-0 font-weight-bold text-gray-800">
                    {pipeStats?.avg_latency_ms?.toFixed(0) || 0}ms
                  </div>
                  <div className="text-xs text-muted mt-1">
                    <i className="fas fa-microchip mr-1"></i>
                    Gateway: {pipeStats?.avg_gateway_processing_ms?.toFixed(0) || 0}ms
                    <br/>
                    <i className="fas fa-brain mr-1"></i>
                    Target: {pipeStats?.avg_target_processing_ms?.toFixed(0) || 0}ms
                  </div>
                </div>
                <div className="col-auto">
                  <i className="fas fa-tachometer-alt fa-2x text-gray-300"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6 mb-4">
          <div className="card border-left-warning shadow h-100 py-2">
            <div className="card-body">
              <div className="row no-gutters align-items-center">
                <div className="col mr-2">
                  <div className="text-xs font-weight-bold text-warning text-uppercase mb-1">
                    Total Cost
                  </div>
                  <div className="h5 mb-0 font-weight-bold text-gray-800">
                    ${pipeStats?.total_cost?.toFixed(4) || '0.0000'}
                  </div>
                  <div className="text-xs text-muted mt-1">
                    Tokens: {(pipeStats?.total_input_tokens || 0).toLocaleString()} in / {(pipeStats?.total_output_tokens || 0).toLocaleString()} out
                  </div>
                </div>
                <div className="col-auto">
                  <i className="fas fa-dollar-sign fa-2x text-gray-300"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Traffic Chart */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card shadow mb-4">
            <div className="card-header py-3">
              <div className="d-flex justify-content-between align-items-center">
                <h6 className="m-0 font-weight-bold text-primary">
                  <i className="fas fa-chart-line mr-2"></i>
                  Pipe Traffic Over Time
                </h6>
                <select
                  className="form-control form-control-sm dropdown-styling"
                  style={{ width: 'auto' }}
                  value={selectedBucket || ''}
                  onChange={handleBucketChange}
                >
                  <option value="10">10 seconds</option>
                  <option value="30">30 seconds</option>
                  <option value="60">1 minute</option>
                  <option value="300">5 minutes</option>
                  <option value="900">15 minutes</option>
                  <option value="3600">1 hour</option>
                </select>
              </div>
            </div>
            <div className="card-body">
              <div style={{ height: '300px', position: 'relative' }}>
                <Line key={`traffic-${chartsKey}`} data={pipeTrafficChartData} options={trafficChartOptions as any} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Latency Over Time Chart */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card shadow mb-4">
            <div className="card-header py-3">
              <div className="d-flex justify-content-between align-items-center">
                <h6 className="m-0 font-weight-bold text-primary">
                  <i className="fas fa-tachometer-alt mr-2"></i>
                  Connection Latency Over Time (p50, p95, p99)
                </h6>
                <select
                  className="form-control form-control-sm dropdown-styling"
                  style={{ width: 'auto' }}
                  value={selectedBucket || ''}
                  onChange={handleBucketChange}
                >
                  <option value="10">10 seconds</option>
                  <option value="30">30 seconds</option>
                  <option value="60">1 minute</option>
                  <option value="300">5 minutes</option>
                  <option value="900">15 minutes</option>
                  <option value="3600">1 hour</option>
                </select>
              </div>
            </div>
            <div className="card-body">
              <div style={{ height: '300px', position: 'relative' }}>
                {pipeLatencyChartData.labels.length > 0 ? (
                  <Line 
                    key={`latency-${chartsKey}`}
                    data={pipeLatencyChartData} 
                    options={latencyChartOptions as any}
                    redraw={false}
                  />
                ) : (
                  <div className="d-flex justify-content-center align-items-center h-100">
                    <p className="text-muted">No latency data available</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Logs Section */}
      <div className="row">
        <div className="col-12">
          <PipeLogsViewer 
            pipeId={pipeId}
            pipeName={pipeStats?.pipe_name || undefined}
          />
        </div>
      </div>
    </div>
  );
};

export default PipeMonitoringTab;
